import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="side-scroller"
export default class extends Controller {
  static targets = ["track", "previousButton", "nextButton", "progress", "progressTrack"]
  static values = { loop: Boolean }

  connect() {
    this.isDragging = false
    this.didDrag = false
    this.startX = 0
    this.startScrollLeft = 0
    this.minimumScrollLeft = 0
    this.loopSegmentWidth = 0
    this.loopOriginScrollLeft = 0
    this.dragThreshold = 4

    this.handlePointerDown = this.pointerDown.bind(this)
    this.handlePointerMove = this.pointerMove.bind(this)
    this.handlePointerUp = this.pointerUp.bind(this)
    this.handleNativeDrag = this.preventNativeDrag.bind(this)
    this.handleScroll = this.updateState.bind(this)
    this.handleResize = this.refreshLayout.bind(this)

    this.scrollElement.addEventListener("pointerdown", this.handlePointerDown)
    this.scrollElement.addEventListener("pointermove", this.handlePointerMove)
    this.scrollElement.addEventListener("dragstart", this.handleNativeDrag)
    this.scrollElement.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleResize)
    window.addEventListener("pointerup", this.handlePointerUp)
    window.addEventListener("pointercancel", this.handlePointerUp)

    requestAnimationFrame(() => {
      this.minimumScrollLeft = this.scrollElement.scrollLeft
      this.refreshLayout()
    })
  }

  disconnect() {
    this.scrollElement.removeEventListener("pointerdown", this.handlePointerDown)
    this.scrollElement.removeEventListener("pointermove", this.handlePointerMove)
    this.scrollElement.removeEventListener("dragstart", this.handleNativeDrag)
    this.scrollElement.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
    window.removeEventListener("pointerup", this.handlePointerUp)
    window.removeEventListener("pointercancel", this.handlePointerUp)
  }

  get scrollElement() {
    return this.hasTrackTarget ? this.trackTarget : this.element
  }

  scrollLeft() {
    this.scrollBy(-1)
  }

  scrollRight() {
    this.scrollBy(1)
  }

  scrollBy(direction) {
    const maxScrollLeft = this.maximumScrollLeft
    if (maxScrollLeft <= this.minimumScrollLeft) return

    const firstCard = this.firstCard
    const distance = firstCard
      ? firstCard.getBoundingClientRect().width + this.trackGap()
      : this.scrollElement.clientWidth * 0.85

    this.scrollElement.scrollBy({
      left: distance * direction,
      behavior: "smooth"
    })
  }

  updateControls() {
    const maxScrollLeft = this.maximumScrollLeft
    const hasOverflow = maxScrollLeft > this.minimumScrollLeft + 4
    const atStart = this.scrollElement.scrollLeft <= this.minimumScrollLeft + 4
    const atEnd = this.scrollElement.scrollLeft >= maxScrollLeft - 4

    if (this.hasPreviousButtonTarget) {
      const disabled = this.canLoopSeamlessly ? false : (hasOverflow ? atStart : true)
      this.previousButtonTarget.disabled = disabled
      this.previousButtonTarget.setAttribute("aria-disabled", String(disabled))
      this.previousButtonTarget.classList.toggle("!hidden", !hasOverflow)
    }

    if (this.hasNextButtonTarget) {
      const disabled = this.canLoopSeamlessly ? false : (hasOverflow ? atEnd : true)
      this.nextButtonTarget.disabled = disabled
      this.nextButtonTarget.setAttribute("aria-disabled", String(disabled))
      this.nextButtonTarget.classList.toggle("!hidden", !hasOverflow)
    }
  }

  updateProgress() {
    if (!this.hasProgressTarget || !this.hasProgressTrackTarget) return

    const viewportWidth = this.scrollElement.clientWidth
    const contentWidth = this.canLoopSeamlessly ? this.loopSegmentWidth + viewportWidth : this.scrollElement.scrollWidth
    const effectiveMaxScrollLeft = this.canLoopSeamlessly ? this.loopSegmentWidth : Math.max(this.maximumScrollLeft - this.minimumScrollLeft, 0)
    const hasOverflow = effectiveMaxScrollLeft > 4

    this.progressTrackTarget.classList.toggle("!hidden", !hasOverflow)
    if (!hasOverflow) {
      this.progressTarget.style.width = "100%"
      this.progressTarget.style.transform = "translateX(0px)"
      return
    }

    const trackWidth = this.progressTrackTarget.clientWidth

    if (contentWidth <= 0 || trackWidth <= 0) {
      this.progressTarget.style.width = "100%"
      this.progressTarget.style.transform = "translateX(0px)"
      return
    }

    const visibleRatio = Math.min(viewportWidth / contentWidth, 1)
    const thumbWidth = Math.max(trackWidth * visibleRatio, 24)
    const maxOffset = trackWidth - thumbWidth
    const scrollLeft = this.canLoopSeamlessly
      ? this.normalizedLoopScrollLeft
      : Math.min(Math.max(this.scrollElement.scrollLeft - this.minimumScrollLeft, 0), effectiveMaxScrollLeft)

    if (scrollLeft <= 4) {
      this.progressTarget.style.width = `${thumbWidth}px`
      this.progressTarget.style.transform = "translateX(0px)"
      return
    }

    const progress = scrollLeft >= effectiveMaxScrollLeft ? 1 : scrollLeft / effectiveMaxScrollLeft

    this.progressTarget.style.width = `${thumbWidth}px`
    this.progressTarget.style.transform = `translateX(${maxOffset * progress}px)`
  }

  updateState() {
    this.normalizeLoopPosition()
    this.updateControls()
    this.updateProgress()
  }

  refreshLayout() {
    const previousNormalizedPosition = this.canLoopSeamlessly ? this.normalizedLoopScrollLeft : 0

    this.measureLoop()

    if (this.canLoopSeamlessly) {
      this.scrollElement.scrollLeft = this.loopOriginScrollLeft + previousNormalizedPosition
    }

    this.updateState()
  }

  preventNativeDrag(event) {
    event.preventDefault()
  }

  pointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return

    this.isDragging = true
    this.didDrag = false
    this.startX = event.clientX
    this.startScrollLeft = this.scrollElement.scrollLeft
  }

  pointerMove(event) {
    if (!this.isDragging) return

    const deltaX = event.clientX - this.startX
    if (!this.didDrag && Math.abs(deltaX) > this.dragThreshold) {
      this.didDrag = true
      this.scrollElement.classList.remove("cursor-grab")
      this.scrollElement.classList.add("cursor-grabbing")
      this.scrollElement.setPointerCapture(event.pointerId)
    }

    if (!this.didDrag) return

    this.scrollElement.scrollLeft = this.startScrollLeft - deltaX
    event.preventDefault()
  }

  pointerUp(event) {
    if (!this.isDragging) return

    this.isDragging = false
    this.scrollElement.classList.remove("cursor-grabbing")
    this.scrollElement.classList.add("cursor-grab")

    if (event.pointerId && this.scrollElement.hasPointerCapture(event.pointerId)) {
      this.scrollElement.releasePointerCapture(event.pointerId)
    }
  }

  preventClick(event) {
    if (!this.didDrag) return

    event.preventDefault()
    event.stopPropagation()
    this.didDrag = false
  }

  trackGap() {
    const row = this.firstCard?.parentElement || this.scrollElement
    const styles = window.getComputedStyle(row)
    return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0
  }

  measureLoop() {
    this.loopSegmentWidth = 0
    this.loopOriginScrollLeft = 0

    if (!this.loopValue) return

    const firstCopyFirstCard = this.scrollElement.querySelector('.pod-array-item[data-loop-copy-index="0"]')
    const secondCopyFirstCard = this.scrollElement.querySelector('.pod-array-item[data-loop-copy-index="1"]')
    const middleCopyFirstCard = this.scrollElement.querySelector('.pod-array-item[data-loop-copy-index="2"]')
    if (!firstCopyFirstCard || !secondCopyFirstCard || !middleCopyFirstCard) return

    this.loopSegmentWidth = secondCopyFirstCard.offsetLeft - firstCopyFirstCard.offsetLeft
    this.loopOriginScrollLeft = middleCopyFirstCard.offsetLeft
  }

  normalizeLoopPosition() {
    if (!this.canLoopSeamlessly) return

    const currentScrollLeft = this.scrollElement.scrollLeft
    const lowerBound = this.loopOriginScrollLeft - (2 * this.loopSegmentWidth)
    const upperBound = this.loopOriginScrollLeft + (2 * this.loopSegmentWidth)

    if (currentScrollLeft < lowerBound) {
      this.scrollElement.scrollLeft = currentScrollLeft + this.loopSegmentWidth
    } else if (currentScrollLeft >= upperBound) {
      this.scrollElement.scrollLeft = currentScrollLeft - this.loopSegmentWidth
    }
  }

  get firstCard() {
    return this.scrollElement.querySelector(".pod-array-item")
  }

  get normalizedLoopScrollLeft() {
    if (!this.canLoopSeamlessly) return 0

    return this.scrollElement.scrollLeft - this.loopOriginScrollLeft
  }

  get canLoopSeamlessly() {
    return this.loopValue && this.loopSegmentWidth > 0
  }

  get maximumScrollLeft() {
    return Math.max(this.scrollElement.scrollWidth - this.scrollElement.clientWidth, this.minimumScrollLeft)
  }
}
